import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  Output,
  EventEmitter,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { LinearShade } from 'src/app/misc/functions';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { fromEvent, Subscription } from 'rxjs';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { take, filter } from 'rxjs/operators';

interface BarMeta {
  mangel: string;
  lage: string;
  umfang: number;
  mengeneinheit: string;
  instandsetzung: string;
}
interface Bar {
  StartPosition?: number;
  EndPosition?: number;
  start_m?: number;
  end_m?: number;
  start?: number;
  end?: number;
  fk?: number;
  disfkL?: number;
  disfkR?: number;
  meta?: BarMeta;
  margin: number;
  color: string;
  metastring: string;
  tTipPos: 'top-left' | 'top-right';
}

@Component({
  selector: 'app-progressbar',
  templateUrl: './progressbar.component.html',
  styleUrls: ['./progressbar.component.scss'],
  imports: [CommonModule, ClarityModule, MrTranslatePipe],
})
export class ProgressbarComponent {
  private readonly mrTranslate = inject(MrTranslatePipe);
  overlay = inject(Overlay);
  viewContainerRef = inject(ViewContainerRef);

  @Output() barclicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() contextMenuClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() barMouseOver: EventEmitter<any> = new EventEmitter<any>();
  @Output() barMouseLeave: EventEmitter<any> = new EventEmitter<any>();

  sub: Subscription;
  @ViewChild('userMenu') userMenu: TemplateRef<any>;
  overlayRef: OverlayRef | null;
  opencontextbar: any;

  open({ x, y }: MouseEvent, bar) {
    this.opencontextbar = bar;
    this.close();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo({ x, y })
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.overlayRef.attach(
      new TemplatePortal(this.userMenu, this.viewContainerRef, {
        $implicit: bar,
      })
    );

    this.sub = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          const clickTarget = event.target as HTMLElement;
          return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
        }),
        take(1)
      )
      .subscribe(() => this.close());
  }

  clickProfil() {
    this.contextMenuClicked.emit({
      row: this.opencontextbar,
      data: this.indata,
    });
    this.close();
  }

  close() {
    this.sub?.unsubscribe();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  progressbarwidth: number;
  @ViewChild('wrapper', { static: true }) wrapper: ElementRef;

  data;
  indata: any;
  dist: number;
  bars: Bar[] = [];
  fkShownOnce = {};
  ratio: number = 1;
  _ostammid: number;
  hasProfil: boolean = false;
  _once: boolean;
  _von: number = 0;
  von: number = 0;
  barlegend: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8];

  @Input('dataIn') set dataIn(dat: any) {
    if (dat) {
      this.indata = dat;
      if (JSON.stringify(dat.zeile) != JSON.stringify(this.data)) {
        this.data = dat.zeile;
        this._ostammid = dat.stammid;
        this._once = dat.once;
        if (dat.von) this._von = dat.von;
        if (document.documentElement.clientWidth > 768) {
          this.barlegend = [0, 1, 2, 3, 4, 5, 6, 7, 8];
        } else {
          this.barlegend = [0, 1, 2, 3, 4];
        }
        this.createBars(dat);
      }
    }
  }
  @Input('indexScrub') indexScrub: number;
  @Output() enlargeProgressbar = new EventEmitter<boolean>();
  @Output() openDialogAt = new EventEmitter<Object>();
  progresses: any[];

  barclick(bar: any) {
    this.barclicked.emit({ row: bar, data: this.indata });
  }

  onMouseOver(bar: any): void {
    this.barMouseOver.emit({ row: bar, data: this.indata });
  }

  onMouseLeave(bar: any): void {
    this.barMouseLeave.emit({ row: bar, data: this.indata });
  }

  createBars(dat: any) {
    this.bars = [];
    this.fkShownOnce = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    this.progressbarwidth = this.wrapper.nativeElement.offsetWidth;

    if (dat.railprof && dat.railprof.length > 0 && this.data.dist) {
      this.hasProfil = true;
      this.ratio = dat.once ? 1 : this.progressbarwidth / this.data.dist;
      this.von = Math.abs(this._von * this.ratio);
      this.dist = this.data.dist;
      dat.railprof.forEach((bar: Bar) => {
        if (bar.EndPosition < this._von + this.dist) {
          bar.start = bar.StartPosition * this.ratio;
          bar.end = bar.EndPosition * this.ratio;
          bar.margin = Math.abs(((bar.start - this.von) / this.progressbarwidth) * 100);
          bar.tTipPos = bar.margin > 75 ? 'top-left' : 'top-right';

          let val = bar[dat.railprofcol];
          const layer = dat.profileslayer.find(u => u.val === val.toString());
          let color = layer.color;
          bar.color = color;
          let s_m: string, e_m: string;
          s_m = bar.StartPosition.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          e_m = bar.EndPosition.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

          bar.metastring = `
            <div class="text-left">
              <strong>${this.mrTranslate.transform('Überfahrten')} ${val}</strong><br/>
              ${s_m}m - ${e_m}m<br/>
              ${this.mrTranslate.transform('Verschleiß Fahrkante links')}: ${bar.disfkL}<br/>
              ${this.mrTranslate.transform('Verschleiß Fahrkante rechts')}: ${bar.disfkR}<br/>
              ${this.mrTranslate.transform('Summer Bearbeitung')}: ${bar[dat.railprofcol]}
            </div>`;

          this.bars.push(bar);
        }
      });
      dat.once = true;
    } else if (this.data?.bars && this.data.dist) {
      this.ratio = dat.once ? 1 : this.progressbarwidth / this.data.dist;
      this.von = Math.abs(this._von * this.ratio);
      this.dist = this.data.dist;
      this.data.bars.forEach((bar: Bar) => {
        let dirDown = false;
        if (bar.end < bar.start) {
          dirDown = true;
          let t = structuredClone(bar.start);
          bar.start = bar.end;
          bar.end = t;
        }

        bar.start_m = bar.start;
        bar.end_m = bar.end;
        if (bar.end - bar.start == 0) bar.end_m += 5;
        bar.start *= this.ratio;
        bar.end = bar.end_m * this.ratio;
        bar.margin = Math.abs(((bar.start - this.von) / this.progressbarwidth) * 100);
        if (dirDown) bar.margin = bar.margin - ((bar.end - bar.start) / this.progressbarwidth) * 100;
        bar.tTipPos = bar.margin > 75 ? 'top-left' : 'top-right';

        let color;
        let alpha: number = 0.8;
        switch (bar.fk) {
          case 1:
            color = 'rgba(255,0,0,' + alpha + ')';
            break;
          case 2:
            color = 'rgba(255,255,0,' + alpha + ')';
            break;
          case 3:
            color = 'rgba(0,255,0,' + alpha + ')';
            break;
          case 4:
            color = 'rgba(0,0,255,' + alpha + ')';
            break;
          default:
            bar.fk = 0;
            color = 'rgba(0,0,0,' + alpha + ')';
            break;
        }
        bar.color = LinearShade(-(this.fkShownOnce[bar.fk] / 10), color);
        let s_m: string, e_m: string;
        if (dat.once) {
          bar.start_m = bar.start_m / (this.progressbarwidth / this.data.dist);
          bar.end_m = bar.end_m / (this.progressbarwidth / this.data.dist);
        }
        s_m = bar.start_m.toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        e_m = bar.end_m.toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        bar.metastring = `
          <div class="text-left">
            <strong>${this.mrTranslate.transform('Fehlerklasse')} ${bar.fk}</strong><br/>
            ${s_m}m - ${e_m}m<br/>
            ${this.mrTranslate.transform('Mangel')}: ${bar.meta.mangel ? bar.meta.mangel : ''}<br/>
            ${this.mrTranslate.transform('Lage')}: ${bar.meta.lage ? bar.meta.lage : ''}<br/>
            ${this.mrTranslate.transform('Umfang')}: ${bar.meta.umfang ? bar.meta.umfang : ''}<br/>
            ${this.mrTranslate.transform('Mengeneinheit')}: ${bar.meta.mengeneinheit ? bar.meta.mengeneinheit : ''}<br/>
            ${this.mrTranslate.transform('Instandsetzungshinweis')}: ${bar.meta.instandsetzung ? bar.meta.instandsetzung : ''}
          </div>`;
        this.fkShownOnce[bar.fk] += 1;
        this.bars.push(bar);
      });
      dat.once = true;
    }
  }
}
